import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Calendar, CalendarCheck, CalendarClock, MapPin, UserPlus, Send, Loader2, CalendarX } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { api, fileUrl, formatApiErrorDetail } from "../lib/api";

const RegisterDialog = ({ event, onClose }) => {
  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post(`/events/${event.id}/register`, form);
      toast.success(data.message || "Registered!");
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not register. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={!!event} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md p-0 overflow-hidden gap-0" data-testid="event-register-dialog">
        <div className="bg-[#0F2942] px-6 py-5 text-white">
          <DialogHeader>
            <DialogTitle className="font-display text-xl text-white flex items-center gap-2">
              <UserPlus className="h-5 w-5 text-amber-400" /> Register for Event
            </DialogTitle>
          </DialogHeader>
          <DialogDescription className="text-sm text-slate-300 mt-1">{event?.title}</DialogDescription>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <div>
            <Label htmlFor="reg-name">Full Name *</Label>
            <Input id="reg-name" required value={form.name} onChange={set("name")} placeholder="Your name"
              data-testid="event-reg-name-input" className="mt-1.5" />
          </div>
          <div>
            <Label htmlFor="reg-phone">Phone Number *</Label>
            <Input id="reg-phone" required value={form.phone} onChange={set("phone")} placeholder="+91 ..."
              data-testid="event-reg-phone-input" className="mt-1.5" />
          </div>
          <div>
            <Label htmlFor="reg-email">Email (optional)</Label>
            <Input id="reg-email" type="email" value={form.email} onChange={set("email")} placeholder="you@email.com"
              data-testid="event-reg-email-input" className="mt-1.5" />
          </div>
          <div>
            <Label htmlFor="reg-msg">Anything else? (optional)</Label>
            <Textarea id="reg-msg" rows={2} value={form.message} onChange={set("message")}
              placeholder="e.g. number of people, questions..." data-testid="event-reg-message-input" className="mt-1.5" />
          </div>
          <button type="submit" disabled={loading} data-testid="event-reg-submit-btn"
            className="w-full inline-flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white font-semibold py-3 rounded-full transition-all">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            {loading ? "Registering..." : "Confirm Registration"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const EventCard = ({ event, onRegister }) => (
  <div data-testid={`event-card-${event.id}`} className="bg-white rounded-2xl overflow-hidden border border-slate-100 shadow-sm card-lift flex flex-col">
    <div className="relative h-52 bg-gradient-to-br from-blue-50 to-emerald-50 overflow-hidden">
      {event.image_path ? (
        <img src={fileUrl(event.image_path)} alt={event.title} className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full flex items-center justify-center"><Calendar className="h-14 w-14 text-slate-300" /></div>
      )}
      <span className={`absolute top-3 left-3 text-white text-[11px] font-semibold px-3 py-1 rounded-full ${event.status === "upcoming" ? "bg-emerald-600" : "bg-slate-500"}`}>
        {event.status === "upcoming" ? "Upcoming" : "Past Event"}
      </span>
    </div>
    <div className="p-6 flex flex-col flex-1">
      <p className="text-sm text-emerald-700 font-semibold flex items-center gap-1.5">
        <Calendar className="h-4 w-4" /> {event.date}
      </p>
      <h3 className="font-display text-xl font-bold text-slate-900 mt-2">{event.title}</h3>
      <p className="mt-2 text-sm text-slate-600 leading-relaxed flex-1 whitespace-pre-wrap">{event.description}</p>
      {event.status === "upcoming" && (
        <button onClick={() => onRegister(event)} data-testid={`event-register-btn-${event.id}`}
          className="mt-5 inline-flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-5 py-2.5 rounded-full transition-all">
          <UserPlus className="h-4 w-4" /> Register
        </button>
      )}
    </div>
  </div>
);

const EventsPage = () => {
  const [tab, setTab] = useState("upcoming");
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [registerFor, setRegisterFor] = useState(null);

  useEffect(() => {
    setLoading(true);
    api.get(`/events?status=${tab}`).then((r) => setEvents(r.data)).catch(() => setEvents([])).finally(() => setLoading(false));
  }, [tab]);

  return (
    <div>
      <section className="bg-[#0F2942] py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="text-xs uppercase tracking-widest font-semibold text-amber-400">Get Involved</span>
          <h1 className="font-display mt-3 text-4xl sm:text-5xl font-extrabold text-white">Our Events</h1>
          <p className="mt-4 text-slate-300 max-w-2xl">
            Join our health camps, education drives and community programmes. Register for upcoming events or
            relive the moments from our past work.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14" data-testid="events-page">
        {/* Tabs */}
        <div className="flex gap-3 mb-10">
          <button onClick={() => setTab("upcoming")} data-testid="events-tab-upcoming"
            className={`inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold transition-all ${tab === "upcoming" ? "bg-emerald-600 text-white shadow-md" : "bg-white text-slate-600 border border-slate-200 hover:border-emerald-300"}`}>
            <CalendarClock className="h-4 w-4" /> Upcoming Events
          </button>
          <button onClick={() => setTab("past")} data-testid="events-tab-past"
            className={`inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold transition-all ${tab === "past" ? "bg-emerald-600 text-white shadow-md" : "bg-white text-slate-600 border border-slate-200 hover:border-emerald-300"}`}>
            <CalendarCheck className="h-4 w-4" /> Past Events
          </button>
        </div>

        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-7">
            {[...Array(3)].map((_, i) => <div key={i} className="h-80 rounded-2xl bg-slate-100 animate-pulse" />)}
          </div>
        ) : events.length === 0 ? (
          <div className="text-center py-24 text-slate-400">
            <CalendarX className="h-14 w-14 mx-auto mb-4" />
            <p className="font-medium">No {tab} events right now.</p>
            <p className="text-sm">{tab === "upcoming" ? "New events will be announced soon — check back!" : "Past events will appear here."}</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-7">
            {events.map((e) => <EventCard key={e.id} event={e} onRegister={setRegisterFor} />)}
          </div>
        )}
      </section>

      {registerFor && <RegisterDialog event={registerFor} onClose={() => setRegisterFor(null)} />}
    </div>
  );
};

export default EventsPage;
